// @ts-check
// Comandos dos fluxos no menu de comandos, no clique direito e na barra de seleção (spec/fluxos.md seção 20.15, item 3;
// contrato 09 de fluxos, seção 17): "Inserir em um fluxo" no card (record.addToFlow) e na seleção (selection.addToFlow)
// abrem o diálogo de inserir leads; "Remover do fluxo" (selection.removeFromFlow) só aparece na lista aberta por um
// contador de fluxo e tira os cards escolhidos daquele fluxo. O registro dos comandos é do passo 0
// (client/src/lib/command-registry.js); aqui ficam a lista dos prontos e a execução.
import { removeLeadsFromFlow } from '../../data/flows.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { MAXIMUM_FLOW_INSERT_IDS } from '../../../../shared/flows/flow-limits.js';
import { isFlowDrilldownRecipe, parseDrilldownParam } from '../../../../shared/dashboards/drilldown-recipe.js';
import { getViewDrilldown } from '../views/view-state.js';
import { requestCommandConfirmation } from '../command-menu/command-state.js';
import { chunkRecordIds, openFlowInsertLeadsDialog } from './flow-insert-leads-dialog.js';
import { t } from '../../i18n/index.js';

/** Comandos dos fluxos já prontos para aparecer. @type {ReadonlySet<string>} */
export const FLOW_COMMAND_IDS = new Set(['record.addToFlow', 'selection.addToFlow', 'selection.removeFromFlow']);

/**
 * Fluxo de onde veio a lista (parâmetro `drilldown` com uma receita de contador de fluxo), ou null.
 * @param {string | null | undefined} parameter
 * @returns {string | null}
 */
export function flowIdOfDrilldownParam(parameter) {
  if (!parameter) return null;
  try {
    const parsed = parseDrilldownParam(parameter);
    return parsed && isFlowDrilldownRecipe(parsed.recipe) ? String(parsed.recipe.flowId) : null;
  } catch {
    return null;
  }
}

/**
 * A lista foi aberta por um clique num contador de fluxo (chip de fluxo, receita do clique com flowId)? Só então
 * "Remover do fluxo" aparece na seleção.
 * @param {import('../../data/metadata-store.js').View | null | undefined} view
 */
export function isFlowDrilldownView(view) {
  if (!view) return false;
  return flowIdOfDrilldownParam(getViewDrilldown(view)) !== null;
}

/**
 * Executa um comando dos fluxos. Devolve false quando o comando não é desta área.
 * @param {import('../../lib/command-registry.js').CommandDescriptor} command
 * @param {import('../command-menu/command-actions.js').CommandRuntime} runtime
 * @returns {Promise<boolean>}
 */
export async function runFlowCommand(command, runtime) {
  if (command.id === 'record.addToFlow') {
    const target = runtime.record;
    if (!target?.record) return true;
    openFlowInsertLeadsDialog({ objectMetadata: target.objectMetadata, recordIds: [target.record.id], onDone: target.reload });
    return true;
  }
  if (command.id === 'selection.addToFlow') {
    const selection = runtime.selection;
    if (!selection) return true;
    // "selecionar todos" de uma lista comum vira público; numa lista de um clique (fotografia) vão os ids
    if (selection.isAllMatchingSelected && !getViewDrilldown(selection.view)) {
      openFlowInsertLeadsDialog({ objectMetadata: selection.objectMetadata, recordIds: null, view: selection.view, onDone: selection.onChanged });
      return true;
    }
    try {
      const recordIds = await selection.resolveSelectedIds();
      openFlowInsertLeadsDialog({ objectMetadata: selection.objectMetadata, recordIds, view: selection.view, onDone: selection.onChanged });
    } catch (error) {
      showErrorSnackbar(error);
    }
    return true;
  }
  if (command.id === 'selection.removeFromFlow') {
    const selection = runtime.selection;
    const flowId = selection ? flowIdOfDrilldownParam(getViewDrilldown(selection.view)) : null;
    if (!selection || !flowId) return true;
    requestCommandConfirmation({
      title: t('flows.commands.removeTitle', { count: selection.count }),
      subtitle: t('flows.commands.removeText'),
      confirmLabel: t('flows.commands.removeConfirm'),
      accent: 'danger',
      onConfirm: async () => {
        try {
          const recordIds = await selection.resolveSelectedIds();
          let removed = 0;
          for (const chunk of chunkRecordIds(recordIds.slice(0, MAXIMUM_FLOW_INSERT_IDS * 50))) removed += (await removeLeadsFromFlow(flowId, chunk)).removed;
          showSuccessSnackbar(t('flows.commands.removed', { count: removed }));
          selection.onChanged();
          selection.onClear();
        } catch (error) {
          showErrorSnackbar(error);
        }
      },
    });
    return true;
  }
  return false;
}
